import React, { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { revokeTicket, getAdminTicketById } from "../../services/tickets"
import QRCode from 'react-qr-code'
import {
    TicketIcon,
    ArrowLeftIcon,
    QrCodeIcon,
    CalendarIcon,
    MapPinIcon,
    UserIcon,
    CurrencyDollarIcon,
    BuildingOfficeIcon,
    ClockIcon
} from "@heroicons/react/24/outline"

const AdminTicketDetails = () => {
    const { ticketId } = useParams()
    const [ticket, setTicket] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const [qrData, setQrData] = useState("")

    const fetchTicket = async () => {
        setLoading(true)
        setError("")
        try {
            const res = await getAdminTicketById(ticketId)
            if (res.status === 200) {
                setTicket(res.data)
            } else {
                setError(res.error || "Ticket not found")
            }
        } catch (err) {
            setError("Failed to fetch ticket")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchTicket() }, [ticketId])

    // Generate QR code data when ticket is loaded
    useEffect(() => {
        if (ticket && ticket.code) {
            const ticketType = ticket.eventId ? 'EVENT' : ticket.offerId ? 'OFFER' : 'UNKNOWN'
            const data = `TICKET:${ticket.code}:${ticket._id || ''}:${ticketType}`
            setQrData(data)
        } else {
            setQrData("")
        }
    }, [ticket])

    const handleRevoke = async () => {
        if (!window.confirm("Revoke this ticket? It will be invalid for entry.")) return
        try {
            const res = await revokeTicket(ticketId)
            if (res.status === 200) {
                setSuccess("Ticket revoked")
                fetchTicket()
            } else {
                setError(res.error || "Failed to revoke")
            }
        } catch (err) {
            setError("Failed to revoke")
        }
    }

    if (loading) return <div className="text-center py-20">Loading...</div>
    if (!ticket) return <div className="text-center py-20">Ticket not found</div>

    return (
        <div className="max-w-2xl mx-auto">
            <Link to="/admin/tickets" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
                <ArrowLeftIcon className="w-4 h-4" /> Back to Tickets
            </Link>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-8">
                {error && <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl">{error}</div>}
                {success && <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-xl">{success}</div>}

                <div className="flex flex-col items-center mb-8">
                    {/* QR Code */}
                    <div className="bg-white p-6 rounded-2xl border-4 border-slate-300 shadow-lg mb-6 flex items-center justify-center">
                        {qrData ? (
                            <div className="p-4 bg-white">
                                <QRCode
                                    value={qrData}
                                    size={200}
                                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                    viewBox={`0 0 200 200`}
                                />
                            </div>
                        ) : (
                            <div className="w-[200px] h-[200px] flex items-center justify-center bg-slate-100 rounded-lg">
                                <QrCodeIcon className="w-12 h-12 text-slate-400" />
                            </div>
                        )}
                    </div>
                    <h1 className="text-3xl font-mono font-bold text-slate-900 tracking-wider">{ticket.code}</h1>
                    <span className={`mt-2 px-3 py-1 rounded-full text-xs font-bold uppercase ${ticket.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                            ticket.status === 'USED' ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-700'
                        }`}>
                        {ticket.status}
                    </span>
                    {qrData && (
                        <p className="mt-4 text-xs text-slate-500 text-center max-w-md">
                            Present this QR code at entry for verification
                        </p>
                    )}
                </div>

                <div className="space-y-6 border-t border-slate-100 pt-6">
                    {/* Ticket Owner Information */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Owner Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-start gap-3">
                                <UserIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                <div>
                                    <p className="text-xs font-bold text-slate-500 uppercase">Owner</p>
                                    <p className="text-slate-900 font-medium">{ticket.attendeeId?.name || 'Unknown'}</p>
                                    {ticket.attendeeId?.email && (
                                        <p className="text-sm text-slate-600">{ticket.attendeeId.email}</p>
                                    )}
                                    {ticket.attendeeId?.phoneNumber && (
                                        <p className="text-sm text-slate-600">{ticket.attendeeId.phoneNumber}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Event/Offer Information */}
                    {ticket.eventId && (
                        <div>
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Event Information</h3>
                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <TicketIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase">Event</p>
                                        <p className="text-slate-900 font-medium">{ticket.eventId.title || 'Unknown'}</p>
                                    </div>
                                </div>
                                {ticket.eventId.startsAt && (
                                    <div className="flex items-start gap-3">
                                        <CalendarIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 uppercase">Start Date</p>
                                            <p className="text-slate-900 font-medium">
                                                {new Date(ticket.eventId.startsAt).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {ticket.eventId.endsAt && (
                                    <div className="flex items-start gap-3">
                                        <ClockIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 uppercase">End Date</p>
                                            <p className="text-slate-900 font-medium">
                                                {new Date(ticket.eventId.endsAt).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {ticket.eventId.venue && (
                                    <div className="flex items-start gap-3">
                                        <MapPinIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 uppercase">Venue</p>
                                            <p className="text-slate-900 font-medium">{ticket.eventId.venue}</p>
                                            {ticket.eventId.location && (ticket.eventId.location.city || ticket.eventId.location.state) && (
                                                <p className="text-sm text-slate-600">
                                                    {[ticket.eventId.location.city, ticket.eventId.location.state, ticket.eventId.location.country].filter(Boolean).join(', ')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {ticket.offerId && (
                        <div>
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Offer Information</h3>
                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <TicketIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase">Offer</p>
                                        <p className="text-slate-900 font-medium">{ticket.offerId.title || 'Unknown'}</p>
                                    </div>
                                </div>
                                {ticket.offerId.discountedPrice && (
                                    <div className="flex items-start gap-3">
                                        <CurrencyDollarIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 uppercase">Price</p>
                                            <p className="text-slate-900 font-medium">₦{ticket.offerId.discountedPrice?.toLocaleString() || '0'}</p>
                                            {ticket.offerId.normalPrice && (
                                                <p className="text-sm text-slate-400 line-through">₦{ticket.offerId.normalPrice.toLocaleString()}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Ticket Type Information */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Ticket Type</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs font-bold text-slate-500 uppercase">Type Name</p>
                                <p className="text-slate-900 font-medium">{ticket.ticketTypeId?.name || 'Standard'}</p>
                                {ticket.ticketTypeId?.description && (
                                    <p className="text-sm text-slate-600 mt-1">{ticket.ticketTypeId.description}</p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-500 uppercase">Price</p>
                                <p className="text-slate-900 font-medium text-lg">
                                    {ticket.ticketTypeId?.currency || '₦'}{ticket.ticketTypeId?.price?.toLocaleString() || '0'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Additional Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {ticket.createdAt && (
                                <div>
                                    <p className="text-xs font-bold text-slate-500 uppercase">Created</p>
                                    <p className="text-slate-900 font-medium">
                                        {new Date(ticket.createdAt).toLocaleString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                            )}
                            {ticket.usedAt && (
                                <div>
                                    <p className="text-xs font-bold text-slate-500 uppercase">Used At</p>
                                    <p className="text-slate-900 font-medium">
                                        {new Date(ticket.usedAt).toLocaleString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                            )}
                            {ticket.orderId && (
                                <div>
                                    <p className="text-xs font-bold text-slate-500 uppercase">Order ID</p>
                                    <p className="text-slate-900 font-medium font-mono text-sm">{ticket.orderId._id || ticket.orderId}</p>
                                    {ticket.orderId.totalAmount && (
                                        <p className="text-sm text-slate-600">Total: ₦{ticket.orderId.totalAmount.toLocaleString()}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {ticket.status === 'ACTIVE' && (
                    <div className="mt-8 pt-6 border-t border-slate-100">
                        <button
                            onClick={handleRevoke}
                            className="w-full py-3 bg-red-50 text-red-600 font-bold rounded-xl hover:bg-red-100 transition"
                        >
                            Revoke Ticket
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AdminTicketDetails
